import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

/**
 * Global click listener that classifies every anchor click on the public
 * site and emits the corresponding GA4 conversion-style event.
 *
 * Handled events:
 *   - phone_click       — any `<a href="tel:...">`
 *   - whatsapp_click    — any anchor pointing to wa.me / whatsapp.com /
 *                         api.whatsapp.com
 *   - directions_click  — any anchor pointing to Google Maps
 *                         (google.com/maps or maps.google)
 *
 * Using a single delegated listener at document capture-phase means:
 *   • no per-component wiring (works for Header, Footer, MobileStickyBar,
 *     Home hero, service pages, Contact page — everywhere);
 *   • future call sites are automatically tracked with no code change;
 *   • the event fires before any framework-level onClick handlers can
 *     preventDefault or stopPropagation swallow it.
 *
 * The lead-submit event (`generate_lead`) is fired from AppointmentForm
 * after a *successful* backend response, not from this listener.
 */
export default function EventTracker() {
  useEffect(() => {
    const handler = (e) => {
      const anchor = e.target && e.target.closest && e.target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!href) return;

      const linkText = (anchor.innerText || anchor.textContent || "").trim().slice(0, 80);
      const pagePath = window.location.pathname + window.location.search;

      if (href.startsWith("tel:")) {
        trackEvent("phone_click", {
          link_url: href,
          link_text: linkText,
          page_path: pagePath,
        });
        return;
      }

      if (
        href.includes("wa.me") ||
        href.includes("whatsapp.com") ||
        href.includes("api.whatsapp")
      ) {
        trackEvent("whatsapp_click", {
          // Send only the destination host, not the pre-filled message, so no
          // patient-side text is transmitted to GA4.
          link_url: href.split("?")[0],
          link_text: linkText,
          page_path: pagePath,
        });
        return;
      }

      if (href.includes("google.com/maps") || href.includes("maps.google")) {
        trackEvent("directions_click", {
          link_url: href.split("?")[0],
          link_text: linkText,
          page_path: pagePath,
        });
        return;
      }
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
}
